// «Настройки → Профиль» (decision 030 п.8, decision 031 п.3): who I am, how the interface looks to me, my password, email and devices;
// for the owner — the transfer of ownership and the export of all data. Every change is a server command with its own checks.

import { useState } from 'react';
import { useMeta, useSync } from '../sync/react.tsx';
import { PageBlock } from '../ui/Page.tsx';
import { Button, Dialog, Field, Input, RadioGroup, Segmented, useToast } from '../ui/primitives.tsx';
import { ProfileHead } from './ProfileHead.tsx';
import { VARIANTS, useVariant, type VariantName } from '../shell/variants.tsx';
import s from '../team/team.module.css';
import p from './settings.module.css';

/** How the interface looks: chosen in this browser, as the variant switcher does (R-U8). */
const LOOKS: VariantName[] = ['theme', 'font', 'density'];

export function ProfilePane() {
  const meta = useMeta();
  const client = useSync();
  const me = meta.user(client.header?.userId);
  if (!me) return null;
  // People from outside (R-A9) and the platform's support come in another way: their password is not changed here.
  const own = !me.external && !me.support;
  return (
    <>
      <ProfileHead key={me.id} me={me} />
      <PageBlock title="Внешний вид" help="Действует в этом браузере." rows>
        {LOOKS.map((n) => <LookRow key={n} name={n} />)}
      </PageBlock>
      {own && <Security />}
    </>
  );
}

function LookRow({ name }: { name: VariantName }) {
  const [v, set] = useVariant(name);
  return (
    <Field as="div" inline="end" label={VARIANTS[name].label}>
      <Segmented size="sm" label={VARIANTS[name].label} value={v} onChange={set} items={VARIANTS[name].options.map(([val, label]) => ({ value: val, label }))} />
    </Field>
  );
}

type Run = (command: string, input: Record<string, unknown>, done: string) => Promise<boolean>;

function useRun(): [Run, boolean, string | null, (e: string | null) => void] {
  const client = useSync();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run: Run = async (command, input, done) => {
    setBusy(true); setError(null);
    const r = await client.run(command, input);
    setBusy(false);
    if (!r.ok) { setError(r.error.message); return false; }
    toast({ kind: 'info', text: done });
    return true;
  };
  return [run, busy, error, setError];
}

/** Which form is open in a dialog: the password, the email, the transfer of ownership. */
type Open = 'password' | 'email' | 'owner' | null;

function Security() {
  const meta = useMeta();
  const client = useSync();
  const me = meta.user(client.header?.userId)!;
  const [open, setOpen] = useState<Open>(null);
  const [run, busy, error] = useRun();
  // B14: the owner hands the account only to a person who has signed in already; with nobody like that the button is off.
  const heirs = meta.users().filter((u) => u.id !== me.id && !u.archivedAt && !u.external && !u.support && !u.pending);
  const close = () => setOpen(null);
  return (
    <>
      <PageBlock title="Вход" rows>
        <Field as="div" inline="end" label="Пароль" hint="После смены другие устройства выйдут из аккаунта.">
          <Button variant="secondary" onClick={() => setOpen('password')}>Сменить</Button>
        </Field>
        <Field as="div" inline="end" label="Email" hint={me.email}>
          <Button variant="secondary" onClick={() => setOpen('email')}>Сменить</Button>
        </Field>
        <Field as="div" inline="end" label="Другие устройства" hint="Завершит вход на всех других устройствах и браузерах. Здесь вы останетесь." error={error}>
          <Button variant="secondary" disabled={busy} onClick={() => void run('user.endSessions', {}, 'Вы вышли на всех других устройствах.')}>Выйти на других</Button>
        </Field>
      </PageBlock>
      {me.role === 'owner' && (
        <PageBlock title="Данные" rows>
          <Field as="div" inline="end" label="Выгрузка всех данных" hint="Структура разделов, все записи, включая архив, и связи между ними одним файлом (JSON Lines). Пароли и ключи не выгружаются, файлы скачиваются в разделе «Файлы». Выгрузка записывается в журнал платформы.">
            <Button variant="secondary" icon="download" onClick={() => location.assign('/api/account/export')}>Выгрузить</Button>
          </Field>
        </PageBlock>
      )}
      {me.role === 'owner' && (
        <PageBlock title="Опасная зона" tone="danger" rows>
          <Field as="div" inline="end" label="Передать владение"
            hint={heirs.length ? 'Владелец один. Вы останетесь в команде с ролью «Администратор»; настройки аккаунта, люди и структура перейдут новому владельцу.' : 'Передать некому: в команде нет людей, которые уже входили в пространство.'}>
            <Button variant="danger" disabled={!heirs.length} onClick={() => setOpen('owner')}>Передать</Button>
          </Field>
        </PageBlock>
      )}
      <Dialog open={open === 'password'} onOpenChange={(o) => { if (!o) close(); }} label="Сменить пароль">{open === 'password' && <PasswordForm id={me.id} onDone={close} />}</Dialog>
      <Dialog open={open === 'email'} onOpenChange={(o) => { if (!o) close(); }} label="Сменить email">{open === 'email' && <EmailForm onDone={close} />}</Dialog>
      <Dialog open={open === 'owner'} onOpenChange={(o) => { if (!o) close(); }} label="Передать владение">{open === 'owner' && <OwnerForm id={me.id} heirs={heirs} onDone={close} />}</Dialog>
    </>
  );
}

const Current = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
  <Field label="Текущий пароль"><Input type="password" autoComplete="current-password" value={value} onChange={(e) => onChange(e.target.value)} required /></Field>
);

/** The foot of a form in a dialog: the error, «Отмена» and the action. */
function Foot({ error, busy, action, danger, onCancel }: { error: string | null; busy: boolean; action: string; danger?: boolean; onCancel: () => void }) {
  return (
    <>
      {error && <p className={p.error} role="alert">{error}</p>}
      <div className={p.foot}><Button onClick={onCancel}>Отмена</Button><Button type="submit" variant={danger ? 'danger' : 'primary'} disabled={busy}>{action}</Button></div>
    </>
  );
}

function PasswordForm({ id, onDone }: { id: string; onDone: () => void }) {
  const [run, busy, error, setError] = useRun();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== again) { setError('Пароли не совпадают'); return; }
    if (await run('user.setPassword', { id, password, current }, 'Пароль изменён. На других устройствах нужно войти заново.')) onDone();
  };
  return (
    <form className={p.form} onSubmit={(e) => void submit(e)} aria-label="Пароль">
      <h2 className={p.formTitle}>Сменить пароль</h2>
      <Current value={current} onChange={setCurrent} />
      <Field label="Новый пароль" hint="Не короче 8 знаков."><Input type="password" autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
      <Field label="Ещё раз"><Input type="password" autoComplete="new-password" minLength={8} value={again} onChange={(e) => setAgain(e.target.value)} required /></Field>
      <p className={p.note}>Другие устройства выйдут из аккаунта.</p>
      <Foot error={error} busy={busy} action="Сменить пароль" onCancel={onDone} />
    </form>
  );
}

function EmailForm({ onDone }: { onDone: () => void }) {
  const [run, busy, error] = useRun();
  const [email, setEmail] = useState('');
  const [current, setCurrent] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run('user.requestEmail', { email, current }, `Письмо отправлено на ${email}. Адрес сменится, когда вы откроете ссылку.`)) onDone();
  };
  return (
    <form className={p.form} onSubmit={(e) => void submit(e)} aria-label="Email">
      <h2 className={p.formTitle}>Сменить email</h2>
      <Field label="Новый email"><Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
      <Current value={current} onChange={setCurrent} />
      <p className={p.note}>Придёт письмо на новый адрес. До подтверждения вход — по прежнему.</p>
      <Foot error={error} busy={busy} action="Отправить письмо" onCancel={onDone} />
    </form>
  );
}

function OwnerForm({ id, heirs, onDone }: { id: string; heirs: { id: string; name: string; email: string }[]; onDone: () => void }) {
  const [run, busy, error, setError] = useRun();
  const [to, setTo] = useState<string>('');
  const [current, setCurrent] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const heir = heirs.find((u) => u.id === to);
    if (!heir) { setError('Выберите человека'); return; }
    if (await run('user.transferOwnership', { to, current }, `Владелец пространства теперь ${heir.name}.`)) onDone();
  };
  return (
    <form className={p.form} onSubmit={(e) => void submit(e)} aria-label="Владение">
      <h2 className={p.formTitle}>Передать владение</h2>
      <Field as="div" label="Новый владелец">
        <RadioGroup label="Новый владелец" value={to} onChange={setTo} options={heirs.map((u) => ({ value: u.id, label: u.name, hint: u.email }))} />
      </Field>
      <Current value={current} onChange={setCurrent} />
      <p className={p.note}>Владелец один. Вы останетесь в команде с ролью «Администратор»; настройки аккаунта, люди и структура перейдут новому владельцу.</p>
      <Foot error={error} busy={busy} action="Передать владение" danger onCancel={onDone} />
    </form>
  );
}
